import React from "react";
import "./DashboardReportWrapper.scss"
import {Dashboard} from "api/model/dashboard/Dashboard";
import ErrorBoundary from "../../ErrorBoundary";
import {DashboardComponentMappingWrapper} from "../dashboard/components/dashboard-utils";
import {DateUtils} from "utils/time-utils";
import {DashboardReportMarginBoxes} from "api/model/dashboard/DashboardReportOptions";
import {ComponentGroupLayoutWrapper} from "../dashboard-report/DashboardReportPage";

declare global {
    interface Window {
        reportGenerationOptions?: {
            companyRef: string,
            dashboardId: string,
            dashboardTitle: string,
            referenceTime: string,
            easasUrl: string,
            scheduleTime: string | null,
            genStartTime: string,
        }
    }
}

type ReportWrapperProps = {
    dashboard: Dashboard,
    referenceTime: string,
    children: JSX.Element | JSX.Element[]
}

const DashboardReportWrapper = (props: {dashboard: Dashboard, referenceTime: string}) => (
    <ReportWrapper dashboard={props.dashboard} referenceTime={props.referenceTime}>
        <React.Fragment>
            { props.dashboard.dashboardTabsJs?.map((c, tabIdx) => {
                return <React.Fragment key={tabIdx}>
                    { c.componentGroups?.map((g, groupIdx) => {
                        return <ComponentGroupLayoutWrapper key={groupIdx} group={g}>

                            { g.components?.map((component, compIdx) => {
                                return <React.Fragment key={compIdx}>
                                    <DashboardReportComponent header={component.title}>
                                        <DashboardComponentMappingWrapper component={component.type} config={component.config} referenceTime={props.referenceTime}/>
                                    </DashboardReportComponent>
                                </React.Fragment>
                            })}
                        </ComponentGroupLayoutWrapper>
                    })}
                </React.Fragment>
            })}
        </React.Fragment>
    </ReportWrapper>
);


const ReportWrapper = ({children, dashboard, referenceTime}: ReportWrapperProps) => {
    const marginBoxes = dashboard.reportOptions.marginBoxes;

    const processVariables = (html: string) => {
        const pattern = dashboard.reportOptions.timingOptions.reportTimeFormat ?? 'yyyy-MM-dd HH:mm';

        const scheduleTimeReplacer = () => {
            if(!window.reportGenerationOptions?.scheduleTime) return 'NOT-SCHEDULED';
            return DateUtils.strFormatDateTime(window.reportGenerationOptions?.scheduleTime, pattern);
        }

        const generationTimeReplacer = () => {
            if(!window.reportGenerationOptions?.genStartTime) return 'NOT-GENERATED';
            return DateUtils.strFormatDateTime(window.reportGenerationOptions?.genStartTime, pattern);
        }

        const referenceTimeReplacer = () => {
            return DateUtils.strFormatDateTime(referenceTime, pattern);
        }

        const replacer: Record<string, string> = {
            'scheduleTime':     scheduleTimeReplacer(),
            'referenceTime':    referenceTimeReplacer(),
            'generationTime':   generationTimeReplacer(),
            'totalPages':       '<span class="page-number-total"></span>',
            'currentPage':      '<span class="page-number-current"></span>',
        }

        let processed = html;
        Object.keys(replacer).forEach(kv => {
            const value = replacer[kv];
            const regex = new RegExp(`\\$${kv}`, 'g');
            processed = processed.replace(regex, value);
        });

        return processed;
    }


    /**
     * Variables prefixed with $ are replaced with builtin values or values from dashboard.reportOptions.variables
     * @param marginBox margin box name to process
     */
    const preformatMarginBox = (marginBox: keyof DashboardReportMarginBoxes) => {
        const box = marginBoxes?.[marginBox];
        const html = box?.content;
        if(!html) return undefined;

        // Fixed variables processing
        // TODO: when there is more builtin values make replacer map struct to cleanup this code
        let processed = processVariables(html)

        // Dynamic variables processing
        if(!box?.variables) return {__html: processed};
        Object.keys(box.variables).forEach(kv => {
            const value = box.variables[kv];
            const regex = new RegExp(`\\$${kv}`, 'g');
            processed = processed.replace(regex, value);
        });

        return {__html: processed}
    }

    return <div id="dashboard-report-wrapper-paged" className="dashboard-report-wrapper-paged print-mode-enabled">
        {/* ==================================================== Margin Boxes ================================================*/}
        <div className="topLeftMarginBox"       dangerouslySetInnerHTML={preformatMarginBox('topLeft')}></div>
        <div className="topCenterMarginBox"     dangerouslySetInnerHTML={preformatMarginBox('topCenter')}></div>
        <div className="topRightMarginBox"      dangerouslySetInnerHTML={preformatMarginBox('topRight')}></div>
        <div className="bottomLeftMarginBox"    dangerouslySetInnerHTML={preformatMarginBox('bottomLeft')}></div>
        <div className="bottomCenterMarginBox"  dangerouslySetInnerHTML={preformatMarginBox('bottomCenter')}></div>
        <div className="bottomRightMarginBox"   dangerouslySetInnerHTML={preformatMarginBox('bottomRight')}></div>

        {/* ==================================================== Dashboad Components ============================================*/}
        {children}
    </div>
}


type DashboardReportComponentProps = {
    header: string,
    children: JSX.Element | JSX.Element[]
}
const DashboardReportComponent = ({header, children}: DashboardReportComponentProps) => {
    return <div className="dashboard-report-component">
        { header &&
            <div className="dashboard-report-component-header">
                <h3>{header}</h3>
            </div>
        }

        <div className="dashboard-report-component-body">
            <ErrorBoundary>
                {children}
            </ErrorBoundary>
        </div>
    </div>
}

export default DashboardReportWrapper;
